import { Outlet, useNavigate } from "react-router";
import useUser from "@app/zustand/user";
import { useMemo } from "react";
import { signOut } from "@app/utils/sign-out";
import WelcomeAnimation from "@app/components/WelcomeAnimation";
import { useLocaleContext } from "@app/contexts/LocaleContext";
import type { AppLocale } from "@app/i18n";
import { cn } from "@app/lib/utils";
import { Trans, useLingui } from "@lingui/macro";

const WELCOME_LOCALES: { code: AppLocale; label: string }[] = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
];

/** The header's language menu needs an account; a visitor picks the language here. */
function WelcomeLocaleSwitcher() {
  const { t } = useLingui();
  const { locale, setAppLocale } = useLocaleContext();
  return (
    <div
      className="absolute right-4 top-4 flex items-center gap-1 text-xs"
      role="group"
      aria-label={t`Langue`}
    >
      {WELCOME_LOCALES.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          title={label}
          aria-pressed={locale === code}
          onClick={() => setAppLocale(code)}
          className={cn(
            "rounded-md px-2 py-1 font-medium transition-colors",
            locale === code
              ? "bg-primary/10 text-primary"
              : "text-slate-400 hover:text-slate-600",
          )}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

const Welcome = () => {
  const user = useUser((state) => state.user);
  const setUser = useUser((state) => state.setUser);
  const navigate = useNavigate();
  const isLoggedIn = useMemo(() => {
    if (!user) return false;
    if (!user.verified_at) return false;
    return true;
  }, [user]);

  return (
    <main className="flex h-screen w-full overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {/* Left panel — hidden on mobile */}
      <div className="hidden md:flex md:w-[55%] relative overflow-hidden border-r border-slate-100/80">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/4 via-blue-50/60 to-slate-50 pointer-events-none" />

        {/* Animation fills the background */}
        <div className="absolute inset-0 opacity-60">
          <WelcomeAnimation />
        </div>

        {/* Centered text overlay */}
        <div className="relative z-10 flex flex-col items-start justify-center h-full w-full px-14 text-left">
          <h2 className="text-5xl font-bold text-slate-900 leading-tight">
            <Trans>Bienvenue sur</Trans>{" "}
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              MAGE-X
            </span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-md">
            <Trans>
              Solutions de structuration de données patients existantes via
              l'IA pour les médecins, la recherche, hôpitaux, cliniques et
              industrie de la santé.
            </Trans>
          </p>
        </div>
      </div>

      {/* Right panel */}
      {/* The access request opens in a dialog, so the column keeps one height and is simply
          centred; `my-auto` rather than `justify-center` keeps its top reachable on short screens. */}
      <div className="relative flex-1 md:w-[45%] flex flex-col items-center px-6 py-10 overflow-auto">
        <WelcomeLocaleSwitcher />
        <div className="my-auto w-full max-w-sm">
          {isLoggedIn ? (
            <div className="flex flex-col gap-4">
              <p className="text-slate-600 text-sm">
                <Trans>Vous êtes connecté.</Trans>
              </p>
              <button
                onClick={() => navigate("/")}
                className="w-full rounded-lg bg-gradient-to-r from-primary to-primary/90 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all"
              >
                <Trans>Accéder au tableau de bord</Trans>
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  signOut(() => {
                    setUser(null);
                    navigate("/welcome");
                  });
                }}
              >
                <button
                  type="submit"
                  className="w-full text-sm text-slate-500 hover:text-primary transition-colors"
                >
                  <Trans>Se déconnecter</Trans>
                </button>
              </form>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </main>
  );
};

export default Welcome;
